/**
 * Drawdown Service for API Communications
 *
 * Handles all API interactions for the drawdown system including:
 * - Capital call management
 * - Payment processing
 * - General ledger operations
 */

import { getTodayAsISODate } from '@/utils/date-operations';
import { apiClient } from '../../utils/api-client';
import type {
  ApiDrawdownInvestor,
  DrawdownCreate,
  DrawdownCreateResponse,
  DrawdownUpdate,
  DrawdownUpdateResponse,
  DrawdownListResponse,
  DrawdownDetailResponse,
  DrawdownStatusUpdate,
  DrawdownPayment,
  DrawdownPaymentUpdate,
  PaymentRecordRequest,
  BulkPaymentRequest,
  PaymentStatistics,
  OverduePaymentInfo,
  OverduePaymentStatus,
  PageResult,
  GeneralLedger,
  LedgerResponse,
  LedgerQueryParams,
  TrialBalance,
  BalanceSheet,
  IncomeStatement,
  GeneratePDFRequest,
  RecordFullPaymentRequest,
  RecordFullPaymentResponse,
} from '../../types/drawdown';
import { mapDrawdownInvestor } from './drawdownMapper';

// Helper function to build query string
function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

export class DrawdownService {
  // ==================== Drawdown Management ====================

  /**
   * Get all drawdowns with optional filtering
   * Now supports both global and fund-scoped queries
   */
  static async getDrawdowns(
    params: {
      skip?: number;
      limit?: number;
      fund_id?: string;
      status?: string;
      include_investors?: boolean;
    } = {}
  ): Promise<DrawdownListResponse> {
    const { fund_id, ...otherParams } = params;
    const queryString = buildQueryString(otherParams);

    // Use fund-scoped endpoint if fund_id is provided
    const endpoint = fund_id
      ? `/funds/${fund_id}/drawdowns?${queryString}`
      : `/drawdowns?${queryString}`;

    return apiClient.get<DrawdownListResponse>(endpoint);
  }

  /**
   * Create a new drawdown
   */
  static async createDrawdown(
    drawdownData: DrawdownCreate,
    fundId?: string
  ): Promise<DrawdownCreateResponse> {
    // Use fund-scoped endpoint if fundId is provided
    const endpoint = fundId ? `/funds/${fundId}/drawdowns` : `/drawdowns`;

    return apiClient.post<DrawdownCreateResponse>(endpoint, drawdownData);
  }

  /**
   * Get a specific drawdown by ID
   * Now supports fund-scoped queries
   */
  static async getDrawdown(
    drawdownId: string,
    includeInvestors: boolean = true,
    includePayments: boolean = true,
    fundId?: string
  ): Promise<DrawdownDetailResponse> {
    const queryString = buildQueryString({
      include_investors: includeInvestors,
      include_payments: includePayments,
    });

    // Use fund-scoped endpoint if fundId is provided
    const endpoint = fundId
      ? `/funds/${fundId}/drawdowns/${drawdownId}?${queryString}`
      : `/drawdowns/${drawdownId}?${queryString}`;

    const response = await apiClient.get<DrawdownDetailResponse>(endpoint);
    response.drawdown.drawdown_investors =
      response.drawdown.drawdown_investors?.map((inv) =>
        mapDrawdownInvestor(inv as unknown as ApiDrawdownInvestor)
      ) ?? [];
    response.overdue_investors =
      response.overdue_investors?.map((inv) =>
        mapDrawdownInvestor(inv as unknown as ApiDrawdownInvestor)
      ) ?? [];
    return response;
  }

  /**
   * Update a drawdown (only if in draft status)
   */
  static async updateDrawdown(
    drawdownId: string,
    drawdownUpdate: DrawdownUpdate
  ): Promise<DrawdownUpdateResponse> {
    return apiClient.put<DrawdownUpdateResponse>(
      `/drawdowns/${drawdownId}`,
      drawdownUpdate
    );
  }

  /**
   * Update drawdown status
   */
  static async updateDrawdownStatus(
    drawdownId: string,
    statusUpdate: DrawdownStatusUpdate,
    fundId?: string
  ): Promise<DrawdownUpdateResponse> {
    // Use fund-scoped endpoint if fundId is provided
    const endpoint = fundId
      ? `/funds/${fundId}/drawdowns/${drawdownId}/status`
      : `/drawdowns/${drawdownId}/status`;

    return apiClient.patch<DrawdownUpdateResponse>(endpoint, statusUpdate);
  }

  /**
   * Delete a drawdown (only if in draft status)
   */
  static async deleteDrawdown(
    drawdownId: string,
    fundId?: string
  ): Promise<{ message: string; drawdown_id: string }> {
    // Use fund-scoped endpoint if fundId is provided
    const endpoint = fundId
      ? `/funds/${fundId}/drawdowns/${drawdownId}`
      : `/drawdowns/${drawdownId}`;

    return apiClient.delete<{ message: string; drawdown_id: string }>(endpoint);
  }

  /**
   * Get payment summary for a drawdown
   */
  static async getDrawdownPaymentSummary(drawdownId: string): Promise<unknown> {
    return apiClient.get(`/drawdowns/${drawdownId}/summary`);
  }

  /**
   * Generate PDF for drawdown notice
   */
  static async generateDrawdownPDF(
    drawdownId: string,
    pdfRequest: GeneratePDFRequest = {
      regenerate: false,
      include_payment_instructions: true,
    }
  ): Promise<{ message: string; drawdown_id: string }> {
    return apiClient.post(`/drawdowns/${drawdownId}/generate-pdf`, pdfRequest);
  }

  /**
   * Download drawdown PDF
   */
  static async downloadDrawdownPDF(drawdownId: string): Promise<Blob> {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`/drawdowns/${drawdownId}/pdf`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      throw new Error(`Failed to download PDF: ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Regenerate all PDFs for all batches in a drawdown
   */
  static async regenerateAllDrawdownPDFs(
    drawdownId: string,
    fundId?: string
  ): Promise<{ message: string; drawdown_id: string }> {
    const endpoint = fundId
      ? `/funds/${fundId}/drawdowns/${drawdownId}/regenerate-all-pdfs`
      : `/drawdowns/${drawdownId}/regenerate-all-pdfs`;

    return apiClient.post<{ message: string; drawdown_id: string }>(
      endpoint,
      null
    );
  }

  /**
   * Download all PDFs from all batches in a drawdown as a ZIP file
   */
  static async downloadAllDrawdownPDFsZip(
    fundId: string,
    drawdownId: string
  ): Promise<void> {
    const url = `/funds/${fundId}/drawdowns/${drawdownId}/pdfs/zip`;
    const blob = await apiClient.getBlob(url);

    // Trigger browser download
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `drawdown-${drawdownId}-all-pdfs.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  }

  /**
   * Send email notifications to all investors in a drawdown
   */
  static async sendDrawdownEmails(
    fundId: string,
    drawdownId: string,
    newOnly: boolean = false
  ): Promise<{
    message: string;
    drawdown_id: string;
    emails_queued: number;
    new_only: boolean;
  }> {
    const endpoint = `/funds/${fundId}/drawdowns/${drawdownId}/send-emails`;
    const queryString = newOnly ? '?new_only=true' : '';

    return apiClient.post<{
      message: string;
      drawdown_id: string;
      emails_queued: number;
      new_only: boolean;
    }>(`${endpoint}${queryString}`, null);
  }

  // ==================== Payment Processing ====================

  /**
   * Record a payment from an investor
   */
  static async recordPayment(
    paymentData: PaymentRecordRequest
  ): Promise<DrawdownPayment> {
    return apiClient.post<DrawdownPayment>(
      `/drawdowns/${paymentData.drawdown_investor_id}/payments`,
      paymentData
    );
  }

  /**
   * Unlink a payment from the investor it was credited to
   */
  static async removePayment(
    fundId: string,
    drawdownId: string,
    paymentId: string
  ): Promise<void> {
    return apiClient.delete(
      `/funds/${fundId}/drawdowns/${drawdownId}/payments/${paymentId}`
    );
  }

  /**
   * Record multiple payments at once
   */
  static async recordBulkPayments(
    drawdownId: string,
    bulkPaymentData: BulkPaymentRequest
  ): Promise<DrawdownPayment[]> {
    return apiClient.post<DrawdownPayment[]>(
      `/drawdowns/${drawdownId}/payments/bulk`,
      bulkPaymentData
    );
  }

  /**
   * Record full payment for a drawdown investor (admin action)
   */
  static async recordInvestorFullPayment(
    fundId: string,
    drawdownId: string,
    drawdownInvestorId: string,
    data: RecordFullPaymentRequest
  ): Promise<RecordFullPaymentResponse> {
    return apiClient.post<RecordFullPaymentResponse>(
      `/funds/${fundId}/drawdowns/${drawdownId}/investors/${drawdownInvestorId}/record-payment`,
      data
    );
  }

  static async updateNoticeAmount(
    fundId: string,
    drawdownId: string,
    drawdownInvestorId: string,
    drawdownAmount: number
  ): Promise<{ id: string }> {
    return apiClient.patch<{ id: string }>(
      `/funds/${fundId}/drawdowns/${drawdownId}/investors/${drawdownInvestorId}`,
      { drawdown_amount: drawdownAmount.toFixed(2) }
    );
  }

  /**
   * Get payments with filtering
   * Now supports fund-scoped queries
   */
  static async getPayments(
    params: {
      skip?: number;
      limit?: number;
      fund_id?: string;
      drawdown_id?: string;
      investor_id?: string;
      processing_status?: string;
      reconciled?: boolean;
    } = {}
  ): Promise<DrawdownPayment[]> {
    const { fund_id, ...otherParams } = params;
    const queryString = buildQueryString(otherParams);

    // Use fund-scoped endpoint if fund_id is provided
    const endpoint = fund_id
      ? `/funds/${fund_id}/payments?${queryString}`
      : `/payments?${queryString}`;

    return apiClient.get<DrawdownPayment[]>(endpoint);
  }

  /**
   * Get a specific payment by ID
   */
  static async getPayment(paymentId: string): Promise<DrawdownPayment> {
    return apiClient.get<DrawdownPayment>(`/payments/${paymentId}`);
  }

  /**
   * Update payment details
   */
  static async updatePayment(
    paymentId: string,
    paymentUpdate: DrawdownPaymentUpdate
  ): Promise<DrawdownPayment> {
    return apiClient.put<DrawdownPayment>(
      `/payments/${paymentId}`,
      paymentUpdate
    );
  }

  /**
   * Mark payment as reconciled
   */
  static async reconcilePayment(
    paymentId: string,
    reconciliationNotes?: string
  ): Promise<{ message: string; payment_id: string }> {
    return apiClient.put(`/payments/${paymentId}/reconcile`, {
      reconciliation_notes: reconciliationNotes,
    });
  }

  /**
   * Mark payment as unreconciled
   */
  static async unreconcilePayment(
    paymentId: string
  ): Promise<{ message: string; payment_id: string }> {
    return apiClient.put(`/payments/${paymentId}/unreconcile`, {});
  }

  /**
   * Get payment statistics
   * Now supports fund-scoped queries
   */
  static async getPaymentStatistics(
    params: {
      fund_id?: string;
      date_from?: string;
      date_to?: string;
    } = {}
  ): Promise<PaymentStatistics> {
    const { fund_id, ...otherParams } = params;
    const queryString = buildQueryString(otherParams);

    // Use fund-scoped endpoint if fund_id is provided
    const endpoint = fund_id
      ? `/funds/${fund_id}/payments/statistics?${queryString}`
      : `/payments/statistics?${queryString}`;

    return apiClient.get<PaymentStatistics>(endpoint);
  }

  /**
   * Get unreconciled payments
   */
  static async getUnreconciledPayments(
    params: {
      skip?: number;
      limit?: number;
      fund_id?: string;
    } = {}
  ): Promise<DrawdownPayment[]> {
    const queryString = buildQueryString(params);
    return apiClient.get<DrawdownPayment[]>(
      `/payments/unreconciled?${queryString}`
    );
  }

  /**
   * Get a page of overdue payments, for one fund or across all of them
   */
  static async getOverduePayments(
    params: {
      page?: number;
      page_size?: number;
      fund_id?: string;
      search?: string;
      status?: OverduePaymentStatus;
    } = {}
  ): Promise<PageResult<OverduePaymentInfo>> {
    const { fund_id, ...otherParams } = params;
    const queryString = buildQueryString(otherParams);

    // Use fund-scoped endpoint if fund_id is provided
    const endpoint = fund_id
      ? `/funds/${fund_id}/payments/overdue?${queryString}`
      : `/payments/overdue?${queryString}`;

    return apiClient.get<PageResult<OverduePaymentInfo>>(endpoint);
  }

  /**
   * Match payment to bank statement line
   */
  static async matchBankStatementLine(
    fundId: string,
    payload: {
      payment_id: string;
      bank_statement_line: string;
      payment_reference: string;
      counterparty_iban: string;
      amount: number;
      currency: string;
    }
  ): Promise<{
    message: string;
    payment_id: string;
    bank_statement_line: string;
  }> {
    return apiClient.post(
      `/funds/${fundId}/payments/bank-reconciliation/match`,
      payload
    );
  }

  // ==================== General Ledger Operations ====================

  /**
   * Get general ledger entries
   * Now supports fund-scoped queries
   */
  static async getLedgerEntries(
    queryParams: LedgerQueryParams
  ): Promise<LedgerResponse> {
    const { fund_id, ...otherParams } = queryParams;
    const queryString = buildQueryString(otherParams);

    // Use fund-scoped endpoint if fund_id is provided
    const endpoint = fund_id
      ? `/funds/${fund_id}/ledger/entries?${queryString}`
      : `/ledger/entries?${queryString}`;

    return apiClient.get<LedgerResponse>(endpoint);
  }

  /**
   * Get trial balance
   * Now supports fund-scoped queries
   */
  static async getTrialBalance(
    params: {
      fund_id?: string;
      as_of_date?: string;
    } = {}
  ): Promise<TrialBalance> {
    const { fund_id, ...otherParams } = params;
    const queryString = buildQueryString(otherParams);

    // Use fund-scoped endpoint if fund_id is provided
    const endpoint = fund_id
      ? `/funds/${fund_id}/ledger/trial-balance?${queryString}`
      : `/ledger/trial-balance?${queryString}`;

    return apiClient.get<TrialBalance>(endpoint);
  }

  /**
   * Create adjustment entry
   */
  static async createAdjustmentEntry(
    fundId: string,
    description: string,
    adjustments: Array<{
      account_code: string;
      debit_amount?: number;
      credit_amount?: number;
      investor_id?: string;
    }>,
    params: {
      document_date?: string;
      posting_date?: string;
      notes?: string;
      reference?: string;
    } = {}
  ): Promise<GeneralLedger[]> {
    return apiClient.post<GeneralLedger[]>(
      `/funds/${fundId}/ledger/adjustments`,
      {
        description,
        adjustments,
        ...params,
      }
    );
  }

  /**
   * Get balance sheet
   * Now supports fund-scoped queries
   */
  static async getBalanceSheet(
    params: {
      fund_id?: string;
      as_of_date?: string;
    } = {}
  ): Promise<BalanceSheet> {
    const { fund_id, ...otherParams } = params;
    const queryString = buildQueryString(otherParams);

    // Use fund-scoped endpoint if fund_id is provided
    const endpoint = fund_id
      ? `/funds/${fund_id}/ledger/balance-sheet?${queryString}`
      : `/ledger/balance-sheet?${queryString}`;

    return apiClient.get<BalanceSheet>(endpoint);
  }

  /**
   * Get income statement
   * Now supports fund-scoped queries
   */
  static async getIncomeStatement(
    params: {
      fund_id?: string;
      date_from?: string;
      date_to?: string;
    } = {}
  ): Promise<IncomeStatement> {
    const { fund_id, ...otherParams } = params;
    const queryString = buildQueryString(otherParams);

    // Use fund-scoped endpoint if fund_id is provided
    const endpoint = fund_id
      ? `/funds/${fund_id}/ledger/income-statement?${queryString}`
      : `/ledger/income-statement?${queryString}`;

    return apiClient.get<IncomeStatement>(endpoint);
  }

  /**
   * Download Financial Statements PDF
   * Downloads a PDF containing Income Statement and Balance Sheet
   */
  static async downloadFinancialStatementsPDF(
    fundId: string,
    asOfDate?: string
  ): Promise<void> {
    const queryString = buildQueryString({ as_of_date: asOfDate });
    const url = `/funds/${fundId}/ledger/financial-statements/pdf?${queryString}`;
    const blob = await apiClient.getBlob(url);

    // Get filename from today's date if not provided
    const dateStr = asOfDate || getTodayAsISODate();

    // Trigger browser download
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `financial-statements-${dateStr}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  }

  /**
   * Get audit trail for a transaction
   */
  static async getAuditTrail(
    referenceId: string,
    referenceType: string
  ): Promise<GeneralLedger[]> {
    const queryString = buildQueryString({
      reference_id: referenceId,
      reference_type: referenceType,
    });

    return apiClient.get<GeneralLedger[]>(`/ledger/audit-trail?${queryString}`);
  }

  /**
   * Get journal entry
   */
  static async getJournalEntry(
    journalEntryNumber: string
  ): Promise<GeneralLedger[]> {
    return apiClient.get<GeneralLedger[]>(
      `/ledger/journal-entries/${journalEntryNumber}`
    );
  }

  /**
   * Get reconciliation report
   */
  static async getReconciliationReport(
    params: {
      fund_id?: string;
      account_code?: string;
      as_of_date?: string;
    } = {}
  ): Promise<unknown> {
    const queryString = buildQueryString(params);
    return apiClient.get(`/ledger/reconciliation-report?${queryString}`);
  }
}
